/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import .                                                                                    **/
/**************************************************************************************************/
import { parseItemsResponse } from './response.parser'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as DataTable from '@/Components/Global/DataTable/template.vue'
import * as MenuBar from '@/Components/Global/MenuBar/template.vue'
import * as PopupTable from '@/Components/Global/PopupTable/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppModules } from '@/Constants/AppModules'
import { Component } from '@/Constants/Component'
import { Server } from '@/Constants/Server'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { ActionField } from '@/Helpers/Components/DataTable/ActionField'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { DataParsers } from '@/Helpers/Utils/DataParsers'
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @import Views/Modules                                                                        **/
/**************************************************************************************************/
import { View41DataParsers } from '@/Views/Modules/CheckList/SetupView/response.parser'

/**************************************************************************************************/
/** @internal Const @name AddonSetup                                                             **/
/**************************************************************************************************/
const AddonSetup = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'AddonSetup', mixins: [MixinBase, MixinComponent],

	components:
	{
		DataTable: DataTable.default,
		MenuBar: MenuBar.default,
		PopupTable: PopupTable.default
	},

	props:
	{
		show: Boolean
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			states:
			{
				_id: null as string | null,
				action: Component.Actions.INSERT,
				items: [] as Array<any>,
				itemIndex: -1,
				itemName: '',
				menuBarSelection: AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS,
				showItemsControls: true,
				showMenuBar: true,
				showPopupTable: false,
				storages: [] as Array<any>,
				title: 'Listado de Evaluaciones'
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		// Establecer los parametros de los componentes 'DataTable'.
		this.DataTable.Set<DataTable.DataTableRef['states']>({ showControls: false })
		this.DataTable.SetPermission('NEW_BUTTON', true)

		// Establecer los parametros de los componentes 'PopupTable/DataTable'.
		this.PopupTable.DataTable.Set<DataTable.DataTableRef['states']>({ isLocalSearch: false, showExportButtons: false })
		this.PopupTable.DataTable.SetMaxHeight(668)
		this.PopupTable.DataTable.SetSelectable(true)

		// Establecer los elementos del componente 'MenuBar'.
		this.MenuBar.SetItems(AppModules.Components.MenuBar.AddonsSetupItemOptions)
		this.MenuBar.Set<MenuBar.MenuBarRef['states']>({ selected: AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS })
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		},

		GetButtonClass() : string
		{
			return this.states.itemIndex === -1 ? 'btn-green' : 'btn-orange'
		},

		GetButtonText() : string
		{
			return this.states.itemIndex === -1 ? 'Agregar' : 'Actualizar'
		},

		GetSubmitButtonText() : string
		{
			return this.states.action === Component.Actions.INSERT ? 'Crear' : 'Actualizar'
		},

		MenuBar() : MenuBar.MenuBarRef
		{
			return this.$refs.menuBar as MenuBar.MenuBarRef
		},

		PopupTable() : PopupTable.PopupTableRef
		{
			return this.$refs.popupTable as PopupTable.PopupTableRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		AddNewItem() : void
		{
			const { itemIndex, itemName } = this.states
			if (itemIndex === -1) this.states.items.push({ name: this.states.itemName || '', critical: true })
			else this.states.items.splice(itemIndex, 1, { name: itemName })

			this.ClearEdit()
			this.ParseItems()
		},

		Clear() : void
		{
			// Selección por Defecto.
			const selected = AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS

			// Reiniciar las propiedades de estado.
			this.states =
			{
				_id: null,
				action: Component.Actions.INSERT,
				items: [],
				itemIndex: -1,
				itemName: '',
				menuBarSelection: selected,
				showItemsControls: true,
				showMenuBar: true,
				showPopupTable: false,
				storages: [],
				title: 'Listado de Evaluaciones'
			}

			// Reiniciar la selección del 'MenuBar'.
			this.MenuBar.Set<MenuBar.MenuBarRef['states']>({ selected })
			this.UpdateDataTableControls(selected)
		},

		ClearEdit() : void
		{
			this.Set<AddonSetupRef['states']>({ itemIndex: -1, itemName: '' })
		},

		async FetchStorages() : Promise<any>
		{
			const response = await Store.dispatch('fetchStorages')
			return response
		},

		InitPermissions() : void
		{
			this.DataTable.SetPermission('ACTION_EDIT', true)
			this.DataTable.SetPermission('ACTION_DELETE', true)
		},

		OnASButtonClick(key: string) : void
		{
			PrintHelper.warning('[AddonSetup]:onASButtonClick() event triggered')
			this.$emit('onASButtonClick', key)
		},

		OnDTButtonClick(key: string, { index, item }: any) : void
		{
			if (key === 'delete')
			{
				const { menuBarSelection } = this.states
				if (menuBarSelection === AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS)
				{
					this.states.items.splice(item.num - 1, 1)
					this.ParseItems()
				}
				else if (menuBarSelection === AppModules.Components.MenuBar.AddonsSetupKeyOptions.STORAGES)
				{
					this.states.storages.splice(index, 1)
					this.ParseStorages()
				}
			}
		},

		OnDTInputCheckBoxChanged({ index, item, value }: any) : void
		{
			this.states.items[index].critical = value
		},

		OnDTInputEntryKeyUp({ index, item, value }: any) : void
		{
			this.states.items[index].name = value
		},

		async OnDTNewButtonClick(preventShowPopupTable: boolean, page = 1, forceRefresh = false) : Promise<void>
		{
			// Validaciones Opcionales según Casos.
			const { DataTable: _dataTable } = this.PopupTable
			if (!preventShowPopupTable) this.Set<AddonSetupRef['states']>({ showPopupTable: true })
			if (forceRefresh) _dataTable.ClearAll()
			_dataTable.SetFetchingState(true)

			// Actualizar el titulo al componente 'PopupTable'.
			this.PopupTable.SetTitle('Selección Ubicación')

			// Eliminar los Registros si se fuerza una Actualización.
			if (forceRefresh) Store.commit('destroyStorages')

			// Aplicar Registros con Paginación.
			const response = await Store.dispatch('fetchStoragesForPage', { forceRefresh, page })
			const SummaryParser = DataParsers.Storages.GetSummaryParser(_dataTable.states.stacked)
			const { fields, items } = SummaryParser(response.data)
			_dataTable.UpdateElementsAndPagination(response.totalPages, fields, items)

			// Aplicar Orden para Columna especifica.
			_dataTable.ResetEmptyText()
			_dataTable.SortOrder('code', 'asc')
		},

		OnMBItemClick(selected: number) : void
		{
			// Cambios según el elemento seleccionado del 'MenuBar'.
			this.UpdateDataTableWrapperElements(selected)

			// Actualizar los Controles del 'DataTable'.
			this.UpdateDataTableControls(selected)

			// El contenido del 'DataTable' debe mutar cuando se seleccione un elemento del 'MenuBar'.
			const isEvaluationsSelected = selected === AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS;
			(isEvaluationsSelected) ? this.ParseItems() : this.ParseStorages()
		},

		OnPTClose() : void
		{
			PrintHelper.warning('[AddonSetup]:onPTClose event triggered')
			this.Set<AddonSetupRef['states']>({ showPopupTable: false })
		},

		OnPTCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			this.OnPTSearchButtonClicked(searchParams, page)
		},

		async OnPTPaginationChanged(page: number) : Promise<void>
		{
			this.OnDTNewButtonClick(false, page)
		},

		OnPTRefreshButtonClick() : void
		{
			this.OnDTNewButtonClick(false, 1, true)
		},

		OnPTSearchButtonClicked(searchParams: any, page = 1) : void
		{
			// Parametros.
			const { DataTable: _dataTable } = this.PopupTable
			const { _idAdminCompany } = Store.getters.getStoredUser
			const { searchKey, searchValue } = searchParams
			const SummaryParser = DataParsers.Storages.GetSummaryParser(_dataTable.IsStacked)

			// Cambio de Estado de Carga.
			_dataTable.SetFetchingState(true)

			// Ejecución de Endpoint Buscar.
			_dataTable.DoInputSearch(Server.Routes.Storages.GetStoragesBySearchFilter, SummaryParser,
				{
					_idAdminCompany,
					itemsPerPage: _dataTable.itemsPerPage,
					page,
					searchKey,
					searchValue
				},
				(response) => response.data.body[0]
			)
		},

		OnRowDoubleClick({ item }: any) : void
		{
			PrintHelper.warning('[AddonSetup]:onRowDoubleClick():Event triggered')
			this.Set<AddonSetupRef['states']>({ showPopupTable: false })

			// Validar que no se ingresen multiples copias de una misma ubicación.
			const { storages } = this.states
			const found = storages.find((x: any) => x._idStorage === item._idStorage)

			// Terminar ejecución si la 'Planta' ya fue ingresada.
			if (found) this.ShowToast('Ubicación ya ingresada', `La ubicación ${ item.code } ya se encuentra ingresada en la tabla!`, 'warning')
			else
			{
				this.states.storages.push(item)
				this.ParseStorages()
			}
		},

		ParseItems() : void
		{
			const _items = this.states.items
			if (_items.length > 0)
			{
				const { fields, items, actions } = parseItemsResponse(_items)
				this.DataTable.SetElements(fields, items, actions)
				return
			}
			this.DataTable.ClearData()
		},

		ParseStorages() : void
		{
			const { storages } = this.states
			if (storages.length > 0)
			{
				// Acción para permitir remover la Ubicación de la Tabla.
				const _actions =
				[
					new ActionField('actions', 'Eliminar')
						.addItem('delete', 'icon').setIcon('times-circle').setPermission('ACTION_DELETE').setVariant('red')
				]

				// Mostrar los datos en la Tabla.
				// const SummaryParser = DataParsers.Storages.GetSummaryParser(this._dataTable.states.stacked)
				const { fields, items, actions } = View41DataParsers.AddonsStoragesParser(storages, _actions)
				this.DataTable.SetElements(fields, items, actions)
				return
			}
			this.DataTable.ClearData()
		},

		UpdateDataTableControls(selected: number) : void
		{
			this.DataTable.Set<DataTable.DataTableRef['states']>(
			{
				showControls: selected === AppModules.Components.MenuBar.AddonsSetupKeyOptions.STORAGES
			})
		},

		UpdateDataTableWrapperElements(selected: number) : void
		{
			const isEvaluationsSelected = selected === AppModules.Components.MenuBar.AddonsSetupKeyOptions.EVALUATIONS
			this.Set<AddonSetupRef['states']>(
			{
				menuBarSelection: selected,
				showItemsControls: isEvaluationsSelected,
				title: isEvaluationsSelected ? 'Listado de Evaluaciones' : 'Ubicaciones Asociadas'
			})
		}
	}
})

/**************************************************************************************************/
/** @export Component @name AddonSetup                                                           **/
/**************************************************************************************************/
export default AddonSetup

/**************************************************************************************************/
/** @export Type @name AddonSetupRef                                                             **/
/**************************************************************************************************/
export type AddonSetupRef = InstanceType<typeof AddonSetup>